import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Types for visitor tracking
export interface VisitorEvent {
  eventType: 'pricing_page_visit' | 'signup_conversion' | 'membership_purchase';
  sessionId: string;
  userId?: string;
  timestamp: Date;
  metadata?: {
    referrer?: string;
    userAgent?: string;
    deviceType?: 'desktop' | 'mobile' | 'tablet';
    plan?: 'monthly' | 'yearly';
    source?: string;
  };
}

// Simple session management
let currentSessionId: string | null = null;

/**
 * Get or create a session ID
 */
function getSessionId(): string {
  if (!currentSessionId) {
    // Check if we have a session ID in sessionStorage
    const storedSessionId = sessionStorage.getItem('rushd_session_id');
    if (storedSessionId) {
      currentSessionId = storedSessionId;
    } else {
      // Create new session ID
      currentSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('rushd_session_id', currentSessionId);
    }
  }
  return currentSessionId;
}

/**
 * Detect device type from user agent
 */
function detectDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/tablet|ipad/.test(userAgent)) {
    return 'tablet';
  }
  
  if (/mobile|android|iphone|ipod|blackberry|windows phone/.test(userAgent)) {
    return 'mobile';
  }
  
  return 'desktop';
}

/**
 * Track visitor events for Rush'd analytics
 */
export const trackVisitorEvent = async (
  eventType: VisitorEvent['eventType'],
  metadata: VisitorEvent['metadata'] = {},
  userId?: string
): Promise<void> => {
  try {
    const sessionId = getSessionId();
    
    const event: Omit<VisitorEvent, 'timestamp'> = {
      eventType,
      sessionId,
      userId,
      metadata: {
        referrer: document.referrer || 'direct',
        userAgent: navigator.userAgent,
        deviceType: detectDeviceType(),
        source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
        ...metadata,
      },
    };

    // Store in Firebase
    await addDoc(collection(db, 'rushd_visitor_events'), {
      ...event,
      timestamp: serverTimestamp(),
    });

    console.log(`Tracked visitor event: ${eventType} for session: ${sessionId}`);
  } catch (error) {
    console.error('Failed to track visitor event:', error);
    // Don't throw error to avoid breaking user experience
  }
};

/**
 * Track Rush'd pricing page visits
 */
export const trackRushdPricingPageVisit = async (): Promise<void> => {
  await trackVisitorEvent('pricing_page_visit', {
    source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
  });
};

/**
 * Track Rush'd membership signup
 */
export const trackRushdSignupConversion = async (
  userId: string,
  plan: 'monthly' | 'yearly'
): Promise<void> => {
  await trackVisitorEvent('signup_conversion', {
    plan,
  }, userId);
};

/**
 * Track Rush'd membership purchase completion
 */
export const trackRushdPurchaseConversion = async (
  userId: string,
  plan: 'monthly' | 'yearly',
  amount: number
): Promise<void> => {
  await trackVisitorEvent('membership_purchase', {
    plan,
    amount,
  }, userId);
};

/**
 * Initialize visitor tracking for the current page
 */
export const initializeVisitorTracking = (): void => {
  // Ensure we have a session ID
  getSessionId();
  
  // Track page load if it's the pricing page
  if (window.location.pathname.includes('/rushd') || window.location.pathname.includes('/pricing')) {
    trackRushdPricingPageVisit();
  }
};

/**
 * Clear session data (useful for testing or logout)
 */
export const clearVisitorSession = (): void => {
  currentSessionId = null;
  sessionStorage.removeItem('rushd_session_id');
};